import {
  ListOrdered,

  PenTool,
  Shield,
  Building2,
  CheckCircle2,
  Clock,
  Zap,
  XCircle,
} from 'lucide-react';
import { useDashboard } from '../hooks/useApi';
import { MetricCard } from '../components/shared/MetricCard';
import { AlertCard } from '../components/shared/AlertCard';
import { DataTable } from '../components/shared/DataTable';
import { StatusBadge } from '../components/shared/StatusBadge';
import { LoadingSpinner } from '../components/shared/LoadingSpinner';
import { ErrorAlert } from '../components/shared/ErrorAlert';
import type { QueueStatusByOrganisation, OrganisationCredits } from '../types/api';

export function DashboardPage() {
  const { data, isLoading, error, refetch } = useDashboard();

  if (isLoading) return <LoadingSpinner label="Ladowanie dashboardu..." />;
  if (error) return <ErrorAlert message={`Blad ladowania: ${error.message}`} onRetry={() => refetch()} />;
  if (!data) return null;

  const { queue, recentFailedCount, activeBatchesCount, sealWarnings, organisationCredits } = data;

  const queueColumns = [
    { key: 'organisationName', header: 'Organizacja', sortable: true },
    {
      key: 'queued',
      header: 'W kolejce',
      sortable: true,
      render: (row: QueueStatusByOrganisation) =>
        row.queued > 0 ? <span className="text-amber-600 dark:text-amber-300 font-medium">{row.queued}</span> : <span className="text-gray-400 dark:text-gray-600">0</span>,
    },
    {
      key: 'processing',
      header: 'Przetwarzane',
      sortable: true,
      render: (row: QueueStatusByOrganisation) =>
        row.processing > 0 ? <span className="text-blue-600 dark:text-blue-300 font-medium">{row.processing}</span> : <span className="text-gray-400 dark:text-gray-600">0</span>,
    },
    {
      key: 'completed',
      header: 'Zakonczone',
      sortable: true,
      render: (row: QueueStatusByOrganisation) =>
        <span className="text-emerald-600 dark:text-emerald-300">{row.completed}</span>,
    },
    { key: 'total', header: 'Razem', sortable: true },
  ];

  const creditColumns = [
    { key: 'name', header: 'Organizacja', sortable: true },
    {
      key: 'subscriptionPlan',
      header: 'Plan',
      render: (row: OrganisationCredits) => (
        <StatusBadge
          label={row.subscriptionPlan || 'Brak'}
          variant={row.subscriptionPlan === 'Professional' ? 'info' : row.subscriptionPlan === 'Trial' ? 'warning' : 'neutral'}
        />
      ),
    },
    {
      key: 'available',
      header: 'Dostepne',
      sortable: true,
      render: (row: OrganisationCredits) => (
        <span className={row.available === 'Unlimited' ? 'text-gray-500 dark:text-gray-400' : 'text-gray-900 dark:text-white font-medium'}>
          {row.available === 'Unlimited' ? 'Bez limitu' : row.available}
        </span>
      ),
    },
    {
      key: 'hasOwnSeal',
      header: 'Pieczec',
      render: (row: OrganisationCredits) =>
        row.hasOwnSeal ? <StatusBadge label="Wlasna" variant="info" /> : <StatusBadge label="Globalna" variant="neutral" />,
    },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Dashboard</h1>
        <p className="text-sm text-gray-500 mt-1">Przeglad stanu platformy Credentium</p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          title="W kolejce"
          value={queue.totalQueued + queue.totalDispatched}
          subtitle="Oczekujace + wysslane"
          icon={<Clock className="h-5 w-5 text-amber-400" />}
          variant={queue.totalQueued > 100 ? 'warning' : 'default'}
        />
        <MetricCard
          title="Przetwarzane"
          value={queue.totalProcessing}
          subtitle="Aktualnie w pipeline"
          icon={<Zap className="h-5 w-5 text-blue-400" />}
          variant="default"
        />
        <MetricCard
          title="Zakonczone (7d)"
          value={queue.totalCompleted}
          subtitle="Ostatnie 7 dni"
          icon={<CheckCircle2 className="h-5 w-5 text-emerald-400" />}
          variant="success"
        />
        <MetricCard
          title="Bledy (24h)"
          value={recentFailedCount}
          subtitle="Ostatnie 24 godziny"
          icon={<XCircle className="h-5 w-5 text-red-400" />}
          variant={recentFailedCount > 0 ? 'danger' : 'default'}
        />
      </div>

      {/* Seal Warnings */}
      {sealWarnings.length > 0 && (
        <div className="space-y-2">
          <h2 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider flex items-center gap-2">
            <Shield className="h-4 w-4" />
            Ostrzezenia pieczeci
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {sealWarnings.map((w, i) => (
              <AlertCard
                key={i}
                title={`${w.sealName}${w.organisationName ? ` (${w.organisationName})` : ''}`}
                message={w.message}
                severity={w.severity}
              />
            ))}
          </div>
        </div>
      )}

      {/* Two columns */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {/* Queue by org */}
        <div className="card">
          <div className="flex items-center gap-2 mb-4">
            <ListOrdered className="h-4 w-4 text-gray-500 dark:text-gray-400" />
            <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Kolejka per organizacja (7d)</h2>
          </div>
          <DataTable
            columns={queueColumns}
            data={queue.byOrganisation}
            compact
            emptyMessage="Brak danych w kolejce"
          />
        </div>

        {/* Org credits */}
        <div className="card">
          <div className="flex items-center gap-2 mb-4">
            <Building2 className="h-4 w-4 text-gray-500 dark:text-gray-400" />
            <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Kredyty organizacji</h2>
          </div>
          <DataTable
            columns={creditColumns}
            data={organisationCredits}
            compact
            emptyMessage="Brak organizacji"
          />
        </div>
      </div>

      {/* Active batches */}
      {activeBatchesCount > 0 && (
        <div className="card-compact flex items-center gap-3">
          <PenTool className="h-4 w-4 text-blue-400" />
          <span className="text-sm text-gray-700 dark:text-gray-300">
            Aktywne batche podpisywania: <span className="font-bold text-gray-900 dark:text-white">{activeBatchesCount}</span>
          </span>
        </div>
      )}
    </div>
  );
}
